import { Link } from "react-router-dom";
import { Check, X, Lock, KeyRound, ShieldCheck, Unplug } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO, { SITE_URL } from "@/components/SEO";
import { getPost } from "@/blog/posts";

const STEPS = [
  {
    icon: KeyRound,
    title: "You tap Connect Instagram",
    text: "You start the connection from your Checkout DM dashboard.",
  },
  {
    icon: Lock,
    title: "You approve on Instagram",
    text: "Instagram shows its own permission screen. You sign in with Instagram, never on our site.",
  },
  {
    icon: ShieldCheck,
    title: "Instagram issues an access token",
    text: "We receive a limited access token. We never receive your password.",
  },
  {
    icon: Unplug,
    title: "You can revoke it any time",
    text: "Remove Checkout DM from your Instagram connected apps and our access ends immediately.",
  },
];

const PERMISSIONS = [
  {
    name: "instagram_business_basic",
    does: "Read basic details of your business account.",
    why: "To identify your shop and show your name and profile picture in the dashboard.",
  },
  {
    name: "instagram_business_manage_messages",
    does: "Read and send messages in conversations with your customers.",
    why: "To run your DM checkout.",
  },
  {
    name: "instagram_business_manage_comments",
    does: "Read and reply to comments on your posts and Reels.",
    why: "To power comment-to-DM automation.",
  },
];

const CAN = [
  "Read and send DMs in conversations with your customers",
  "Read comments and reply to them for comment-to-DM",
  "Read basic details of your business account",
];

const NEVER = [
  "Your Instagram password",
  "The ability to post content, follow accounts or edit your profile",
  "Your customers' card details. Payments use secure payment links and we never store card numbers",
];

export default function Security() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Security and Safe Automation | Checkout DM",
    url: `${SITE_URL}/security`,
    description:
      "How Checkout DM connects to Instagram through Meta's official login, what it can and cannot access, and how you stay in control.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: SITE_URL },
  };

  return (
    <>
      <SEO
        title="Security & Safe Automation | Checkout DM"
        description="How Checkout DM connects to Instagram through Meta's official login, what it can and cannot access, and how you stay in control of your account."
        path="/security"
        structuredData={schema}
      />
      <Navbar />

      <section className="px-6 pb-10 pt-16 md:px-12 md:pt-24">
        <div className="mx-auto max-w-5xl">
          <h1 className="max-w-3xl text-balance font-display text-4xl leading-[0.98] tracking-tighter md:text-6xl">
            Security and safe automation.
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink/70">
            You are trusting us with the account your business runs on. Here is exactly how
            Checkout DM connects to it, what it can and cannot do, and how you stay in control.
          </p>
        </div>
      </section>

      {/* How connecting works */}
      <section className="px-6 py-10 md:px-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-display text-3xl tracking-tight md:text-4xl">How connecting works</h2>
          <p className="mt-3 max-w-2xl text-ink/70">
            Checkout DM uses Instagram Business Login, Meta&rsquo;s official OAuth sign-in. It is the
            same &ldquo;log in with&rdquo; pattern you use across the web, so a tool never needs your
            password.
          </p>
          <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <li key={s.title} className="rounded-3xl border border-ink/10 bg-white p-6">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-ink text-cream">
                  <s.icon size={20} aria-hidden="true" />
                </span>
                <p className="mt-4 text-xs font-bold text-ink/40">Step {i + 1}</p>
                <h3 className="mt-1 font-display text-lg leading-snug">{s.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-ink/65">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Access */}
      <section className="px-6 py-10 md:px-12">
        <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
          <div className="rounded-[2rem] bg-mint/45 p-8">
            <h2 className="font-display text-2xl tracking-tight">What Checkout DM can do</h2>
            <ul className="mt-5 space-y-3">
              {CAN.map((t) => (
                <li key={t} className="flex gap-3 text-[16px] leading-relaxed text-ink/80">
                  <Check size={20} className="mt-0.5 flex-none text-ink" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-[2rem] bg-peach/30 p-8">
            <h2 className="font-display text-2xl tracking-tight">What we never get</h2>
            <ul className="mt-5 space-y-3">
              {NEVER.map((t) => (
                <li key={t} className="flex gap-3 text-[16px] leading-relaxed text-ink/80">
                  <X size={20} className="mt-0.5 flex-none text-ink" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Permissions */}
      <section className="px-6 py-10 md:px-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-display text-3xl tracking-tight md:text-4xl">The permissions we use</h2>
          <p className="mt-3 max-w-2xl text-ink/70">
            Checkout DM uses three Instagram permissions, and all three have been approved through
            Meta&rsquo;s App Review. We do not request anything beyond what the features need.
          </p>
          <div className="mt-6 overflow-x-auto rounded-3xl border border-ink/10 bg-white">
            <table className="w-full min-w-[640px] border-collapse text-left text-[15px]">
              <thead>
                <tr className="bg-ink text-cream">
                  <th className="px-5 py-3.5 font-display text-sm font-bold">Permission</th>
                  <th className="px-5 py-3.5 font-display text-sm font-bold">What it allows</th>
                  <th className="px-5 py-3.5 font-display text-sm font-bold">Why we need it</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10">
                {PERMISSIONS.map((p) => (
                  <tr key={p.name}>
                    <td className="px-5 py-4 align-top font-mono text-[13px] font-bold text-ink">{p.name}</td>
                    <td className="px-5 py-4 align-top text-ink/75">{p.does}</td>
                    <td className="px-5 py-4 align-top text-ink/75">{p.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Rules + control + payments */}
      <section className="px-6 py-10 md:px-12">
        <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
          <div className="rounded-3xl border border-ink/10 bg-white p-7">
            <h2 className="font-display text-xl tracking-tight">Staying inside Instagram&rsquo;s rules</h2>
            <ul className="mt-4 space-y-3 text-[15px] leading-relaxed text-ink/70">
              <li>We use only Instagram&rsquo;s official platform. No scraping, no password automation, no browser bots.</li>
              <li>Conversations start with the customer: a message, a comment or a Story reply.</li>
              <li>We respect Instagram&rsquo;s 24-hour messaging window and do not message outside it.</li>
            </ul>
          </div>
          <div className="rounded-3xl border border-ink/10 bg-white p-7">
            <h2 className="font-display text-xl tracking-tight">You stay in control</h2>
            <ul className="mt-4 space-y-3 text-[15px] leading-relaxed text-ink/70">
              <li>
                Remove access any time from your{" "}
                <a
                  href="https://www.instagram.com/accounts/manage_access/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-hotpink underline underline-offset-4"
                >
                  Instagram connected apps
                </a>
                . This immediately revokes our access token.
              </li>
              <li>Reconnect later and your catalog, orders and settings are still there.</li>
              <li>
                Ask for your data to be deleted. See{" "}
                <Link to="/data-deletion" className="font-semibold text-hotpink underline underline-offset-4">
                  Data Deletion
                </Link>
                .
              </li>
            </ul>
          </div>
          <div className="rounded-3xl border border-ink/10 bg-white p-7">
            <h2 className="font-display text-xl tracking-tight">Payments and data</h2>
            <ul className="mt-4 space-y-3 text-[15px] leading-relaxed text-ink/70">
              <li>Payments go directly to your own account. Checkout DM does not hold your money.</li>
              <li>Customer card details are never stored by us.</li>
              <li>Credentials are stored encrypted, and our site is served over HTTPS.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Honest note */}
      <section className="px-6 pb-24 pt-10 md:px-12">
        <div className="mx-auto max-w-5xl rounded-[2rem] bg-ink p-8 text-cream md:p-12">
          <h2 className="font-display text-3xl leading-tight tracking-tight md:text-4xl">
            What we will not promise
          </h2>
          <p className="mt-4 max-w-3xl text-[17px] leading-relaxed text-cream/75">
            No tool can honestly promise that an Instagram account will never be restricted.
            Instagram sets its own policies and sending limits, and we cannot override them. What we
            can promise is how we work: official, permission-based access only, no password ever,
            and a design built around Instagram&rsquo;s rules. And like every online service, no
            system is 100% secure, so we keep our safeguards under review.
          </p>
          <p className="mt-5 text-[16px] text-cream/75">
            Questions about security?{" "}
            <a href="mailto:support@checkoutdm.com" className="font-semibold text-white underline underline-offset-4">
              support@checkoutdm.com
            </a>{" "}
            &middot;{" "}
            <Link to="/privacy-policy" className="font-semibold text-white underline underline-offset-4">
              Privacy Policy
            </Link>{" "}
            {getPost("is-checkout-dm-safe-instagram-oauth") && (
              <>
                {" "}&middot;{" "}
                <Link to="/blog/is-checkout-dm-safe-instagram-oauth" className="font-semibold text-white underline underline-offset-4">
                  Read: Is Checkout DM safe?
                </Link>
              </>
            )}
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
}
